import { FeeGroupings, ProcessorFeeGrouping } from '../fee-groupings/fee-groupings.model';

export enum FlaggedStatementEnum {
    LOW_CONFIDENCE = 'LOW_CONFIDENCE',
    UNRECOGNIZED_FEES = 'UNRECOGNIZED_FEES',
    SYNTHETIC_FEES = 'SYNTHETIC_FEES'
};

export class UserMessage {
    title: string | null = null;
    message: string | null = null;
    id: FlaggedStatementEnum | null = null;
}


export enum StatementStatusEnum {
    PROCESSING = 'processing',
    USER_REVIEW = 'user_review',
    ADMIN_REVIEW = 'admin_review',
    NEW = 'new',
    OPEN = 'open',
    COMPLETED = 'completed',
    DELETED = 'deleted',
    SAVED = 'saved'
}

export enum StatementProcessorEnum {
    FIRST_DATA = 'firstdata',
    WORLD_PAY = 'worldpay',
    TSYS = 'tsys',
    HEARTLAND = 'heartland',
    ELAVON = 'elavon',
    PAYMENTECH = 'paymentech',
    BRAINTREE = 'braintree',
    PAYPAL = 'paypal',
    QUICKBOOKS = 'quickbooks',
    NORTH_AMERICAN_BANCARD = 'north_american_bancard',
    FORTISPAY = 'fortispay',
    UNKNOWN = 'UNKNOWN'
}

export enum StatementStructureTypeEnum {
    costPlus = 'cost',
    flatRate = 'flat',
    tiered = 'tiered'
}

/** Minimal statement shape for creating merchant with blank statement flow; only processor fees are required/checked. */
export class BlankStatement {
    id: string | null = null;
    merchantId: string | null = null;
    merchantName: string | null = null;
    friendlyName: string | null = null;
    status: StatementStatusEnum | null = null;
    feeGroupings: FeeGroupings | null = null;

    constructor(blankStatement: Partial<Statement>) {
        // Only check for processor fees
        const processor = blankStatement?.feeGroupings?.processor;
        if (processor == null || (processor as ProcessorFeeGrouping)?.fees == null) {
            throw new Error('Blank statement must have feeGroupings.processor with fees');
        }
        Object.assign(this, blankStatement);
        this.feeGroupings = new FeeGroupings(blankStatement.feeGroupings ?? {});
    }

    // Inside BlankStatement class
    toStatement(): Statement {
        const now = new Date();
        const defaults: Partial<Statement> = {
            hash: undefined,
            createdAt: now,
            updatedAt: now,
            isDeleted: false,
            processor: undefined,
            processorFormat: undefined,
            organizationId: undefined,
            contactEmail: undefined,
            contactName: undefined,
            ownerEmail: undefined,
            ownerName: undefined,
            fileName: undefined,
            storageBucket: undefined,
            storageFilePath: undefined,
            statementFileUrl: undefined,
            uploadUserUid: undefined,
            totalGrossSales: undefined,
            structure: undefined,
            notes: undefined,
            userMessage: undefined,
            messagedDisplayed: undefined,
        };
        const merged = { ...defaults, ...this } as Partial<Statement>;
        return new Statement(merged);
    }
}

export class Statement {
    constructor(statement: Partial<Statement>) {
        Object.keys(this).forEach(key => {
            if (this[key as keyof Statement] == null && statement[key as keyof Partial<Statement>] == null)
                throw new Error(`${key} cannot be null`);
        });
        Object.assign(this, statement);
        // This is intended to ensure that the expected methods are defined
        this.feeGroupings = new FeeGroupings(this.feeGroupings ?? {});
    }

    id: string | null = null;
    // TODO (jacob): I don't want this to be optional
    hash: string | undefined;
    createdAt: Date | null = null;
    updatedAt: Date | null = null;
    isDeleted: boolean | null = null;
    status: StatementStatusEnum | null = null;
    friendlyName: string | null = null;
    processor: StatementProcessorEnum | undefined;
    processorFormat: string | undefined;

    merchantId: string | null = null;
    organizationId: string | undefined;
    merchantName: string | null = null;
    contactEmail: string | null = null;
    contactName: string | null = null;
    ownerEmail: string | null = null;
    ownerName: string | null = null;

    fileName: string | null = null; // uploaded file name
    storageBucket: string | undefined;
    storageFilePath: string | undefined;
    statementFileUrl: string | null = null;
    // TODO (jacob): I don't want this to be optional
    uploadUserUid: string | undefined;

    totalGrossSales: number | undefined;
    structure: StatementStructureTypeEnum | null = null;

    notes: string | null = null;
    userMessage: UserMessage | undefined;
    messagedDisplayed: boolean | undefined;

    showMerchant: boolean | undefined;
    showAllStatements: boolean | undefined;
    feeGroupings: FeeGroupings | null = null;

    // fields for rate and total fees
    claudeProcessingVolume: number | undefined;
    claudeProcessingVolumeSource: string | undefined;
    claudeVolumeTotal: number | undefined;
    claudeVolumeGap: number | undefined;
    claudeVolumeGapPct: number | undefined;
    processorVolumeTotal: number | undefined;
    effectiveRate: number | undefined;
    effectiveRateMarginOfError: number | undefined;
    effectiveRateByClaudeVolume: number | undefined;
    effectiveRateByProcessorVolume: number | undefined;
    effectiveRateRequiresReview: boolean | undefined;
    effectiveRateNote: string | undefined;
    processingError: { code: string; message: string; retryable: boolean } | null | undefined;

    createdBy?: string;
    groupId?: string;
    teamId?: string;
}

// TODO (jacob): Remove this
interface PageDataObject {
    pageNumber: number;
    rotation?: number;
    viewBox?: Array<number>;

    dimension?: any;
    layout?: any;
}

// TODO (jacob): Remove this
export interface LineItem {
    fromSection: string;
    description: string;
    data: any;
}

// TODO (jacob): Remove this
export interface Prediction {
    feeLabel: string;
    feeGrouping: string;
    textContent: string;
    textExtraction: any;
}
